/**
 * FirmAvatar — shows a firm's logo if logo_url is set,
 * otherwise a colored circle with initials.
 * Colors and initials are deterministic per slug.
 */

interface FirmAvatarConfig {
  initials: string;
  bg: string;      // Tailwind bg class
  text: string;    // Tailwind text class
}

const FIRM_CONFIG: Record<string, FirmAvatarConfig> = {
  'apex-trader-funding': { initials: 'ATF', bg: 'bg-blue-600',    text: 'text-white' },
  'tradeify':            { initials: 'TFY', bg: 'bg-violet-600',  text: 'text-white' },
  'fundednext-futures':  { initials: 'FNF', bg: 'bg-emerald-600', text: 'text-white' },
  'take-profit-trader':  { initials: 'TPT', bg: 'bg-amber-500',   text: 'text-white' },
  'earn2trade':          { initials: 'E2T', bg: 'bg-rose-600',    text: 'text-white' },
  'alpha-futures':       { initials: 'AFX', bg: 'bg-cyan-600',    text: 'text-white' },
  'lucid-trading':       { initials: 'LTD', bg: 'bg-yellow-500',  text: 'text-gray-900' },
  'top-one-futures':     { initials: 'TOF', bg: 'bg-indigo-600',  text: 'text-white' },
};

function getFirmConfig(slug: string | undefined | null, name: string | undefined | null): FirmAvatarConfig {
  if (slug && FIRM_CONFIG[slug]) return FIRM_CONFIG[slug];
  // Fallback: derive initials from name
  const initials = (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((w) => w[0].toUpperCase())
    .join('');
  return { initials, bg: 'bg-gray-500', text: 'text-white' };
}

interface FirmAvatarProps {
  slug?: string | null;
  name?: string | null;
  logoUrl?: string | null;
  /** Tailwind size classes, e.g. "w-10 h-10". Defaults to "w-10 h-10" */
  size?: string;
  /** Extra classes on the outer div */
  className?: string;
  /** Font size class for initials. Defaults to "text-xs" */
  fontSize?: string;
}

export function FirmAvatar({
  slug,
  name,
  logoUrl,
  size = 'w-10 h-10',
  className = '',
  fontSize = 'text-xs',
}: FirmAvatarProps) {
  if (logoUrl) {
    return (
      <div className={`${size} rounded-full overflow-hidden flex-shrink-0 ${className}`}>
        <img
          src={logoUrl}
          alt={name ?? 'Firm logo'}
          loading="lazy"
          className="w-full h-full object-contain"
        />
      </div>
    );
  }

  const config = getFirmConfig(slug, name);

  return (
    <div
      className={`${size} ${config.bg} rounded-full flex items-center justify-center flex-shrink-0 ${className}`}
    >
      <span className={`${fontSize} font-bold tracking-tight ${config.text}`}>
        {config.initials}
      </span>
    </div>
  );
}
